import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import Box from '@mui/material/Box';
import Container from '@mui/material/Container';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import React, { memo, useCallback, useEffect, useMemo, useState } from 'react';
import { Helmet } from 'react-helmet';
import { FormattedMessage, useIntl } from 'react-intl';
import { useLocation, useNavigate } from 'react-router';

import heroBackgroundImageJpg from '../../assets/VS_Background.jpg';
import heroBackgroundImageWebp from '../../assets/VS_Background.jpg.webp';
import FlagButtons from '../../components/BasicUI/Button/FlagButtons';
import config from '../../config';
import HeroSearchForm from '../../forms/HeroSearchForm/HeroSearchForm';
import routeConfiguration from '../../routeConfiguration';
import useAppSelector from '../../util/hooks/useAppSelector';
import useAppTheme from '../../util/hooks/useAppTheme';
import usePass from '../../util/hooks/usePass';
import useScreenSize from '../../util/hooks/useScreenSize';
import useSticky from '../../util/hooks/useSticky';
import { isCordova } from '../../util/mobile/deviceDetection';
import { createResourceLocatorString } from '../../util/routes';
import { parse } from '../../util/urlHelpers';

const heroSearchSx = {
  width: '100%',
  marginLeft: 'auto',
  marginRight: 'auto',
  display: 'flex',
  flexDirection: 'row',
  backgroundColor: 'white',
  alignItems: 'center',
  borderRadius: '50px',
  boxShadow: '0px 0px 24px 0px rgba(0, 0, 0, 0.05)',
  border: '1px solid #d3d3d7',
};

const createHeroSectionStyling = (theme) => ({
  display: 'flex',
  flexDirection: 'column',
  height: !isCordova()
    ? {
        xs: `calc(30vh - ${theme.globals.bottomBarHeight}px)`,
        md: `calc(30vh - ${theme.globals.topBarHeightDesktop}px)`,
        lg: `calc(10vh - ${theme.globals.topBarHeightDesktop}px)`,
      }
    : `calc(100vh - ${theme.globals.bottomBarHeight}px)`,
  minHeight: { xs: '500px' },
  position: 'relative',
  justifyContent: 'center',
  width: 1,
});

const downArrowStyle = {
  display: 'flex',
  color: 'white',
  justifyContent: 'center',
  width: 1,
};

const SectionHeroComponent = () => {
  const intl = useIntl();
  const theme = useAppTheme();
  const navigate = useNavigate();
  const location = useLocation();
  const { isDesktop, isMobile } = useScreenSize();
  const [passValid] = usePass();
  const yellowBannerState = useAppSelector((state) => state.ui.yellowBannerState);

  const { address, origin, bounds } = parse(location.search, {
    latlng: ['origin'],
    latlngBounds: ['bounds'],
  });

  const handleSubmit = ({ location, dates, state }) => {
    const searchParams = {
      dates: dates,
      address: location ? location.address : '',
      bounds: location ? location.bounds : '',
    };
    if (location || dates) {
      navigate(
        createResourceLocatorString(
          'SearchPage',
          routeConfiguration(),
          {},
          { ...searchParams, ...state },
          intl.locale
        ),
        { state }
      );
    }
  };

  const locationFieldsPresent = config.sortSearchByDistance
    ? address && origin && bounds
    : address && bounds;
  const initialSearchFormValues = {
    location: locationFieldsPresent
      ? {
          search: address,
          selectedPlace: { address, origin, bounds },
        }
      : null,
  };

  // Herosearch Scroll Definitions

  const [formEl, setFormEl] = useState(null);
  const formRef = useCallback((el) => {
    if (el) setFormEl(el);
  }, []);

  const heroSearchFromHeight = 72;
  const heroSearchFromTopPadding = 5;
  const topBarHeightDesktop = theme.globals.topBarHeightDesktop;
  const annualPassBarHeightDesktop =
    config.locale === intl.locale && yellowBannerState ? 44 : 0;
  const annualPassBarHeightMobile = 100;
  const noTopBar = isMobile || isCordova();
  const topBarHeightTotal = topBarHeightDesktop + annualPassBarHeightDesktop;
  const topBarHeightMobile = noTopBar
    ? 0
    : !isCordova() && yellowBannerState
      ? annualPassBarHeightMobile
      : 0;

  const topDistance = heroSearchFromHeight + heroSearchFromTopPadding;

  const isSticky = useSticky(
    formEl,
    isDesktop ? topDistance + topBarHeightTotal : topDistance + topBarHeightMobile
  );

  const [formHeight, setFormHeight] = useState(0);
  const [formWidth, setFormWidth] = useState(1);
  const desktopJumpPosition =
    heroSearchFromHeight + annualPassBarHeightDesktop + heroSearchFromTopPadding;
  const mobileJumpPosition = topBarHeightMobile + heroSearchFromTopPadding;

  useEffect(() => {
    const el = formEl;
    if (!el) return;

    const measure = ({ width, height }) => {
      setFormWidth(Math.round(width));
      setFormHeight(Math.round(height));
    };

    // Same missing-ResizeObserver crash as StoryblokHerosearchPreset
    // (VANSITE-FRONTEND-4PN); this copy sits on the landing page hero.
    if (typeof ResizeObserver === 'undefined') {
      measure(el.getBoundingClientRect());
      return;
    }

    const observer = new ResizeObserver((entries) => {
      measure(entries[0].contentRect);
    });
    observer.observe(el);
    return () => observer.disconnect();
  }, [formEl]);

  const heroSectionStyling = useMemo(() => createHeroSectionStyling(theme), [theme]);

  const heading = (
    <Typography color="white" component="h1" textAlign="center" variant="h3">
      <FormattedMessage id="SectionHero.title" />
    </Typography>
  );

  const subHeading = (
    <Typography color="white" component="h2" textAlign="center" variant="h6">
      <FormattedMessage id="SectionHero.subTitle" />
    </Typography>
  );

  const searchBar = (
    <Box display="flex" justifyContent="center">
      <Box
        ref={formRef}
        sx={{
          position: 'relative',
          width: { xs: 1, md: 0.7 },
          height: `${heroSearchFromHeight}px`,
        }}
      >
        <Box
          data-testid="hero-search"
          sx={{
            position: isSticky ? 'fixed' : 'relative',
            top: isSticky
              ? isDesktop
                ? `${desktopJumpPosition}px`
                : `${mobileJumpPosition}px`
              : 'auto',
            zIndex: theme.globals.zIndexHeroScrollBar,
            width: formWidth,
          }}
        >
          <HeroSearchForm
            initialValues={initialSearchFormValues}
            onSubmit={handleSubmit}
            sx={heroSearchSx}
          />
        </Box>
        {isSticky ? <Box height={formHeight} /> : null}
      </Box>
    </Box>
  );

  const downArrowCordova = isCordova() ? (
    <Box mt={4} sx={downArrowStyle}>
      <KeyboardArrowDownIcon
        sx={{
          fontSize: 60,
        }}
      />
    </Box>
  ) : null;

  return (
    <Container maxWidth={false} sx={heroSectionStyling}>
      <picture
        style={{
          position: 'absolute',
          top: 0,
          left: 0,
          width: '100%',
          height: '100%',
          zIndex: -2,
        }}
      >
        <source srcSet={heroBackgroundImageWebp} type="image/webp" />
        <img
          alt="Hero Background"
          fetchpriority="high"
          src={heroBackgroundImageJpg}
          style={{
            width: '100%',
            height: '100%',
            objectFit: 'cover',
          }}
        />
      </picture>
      <Box
        sx={{
          position: 'absolute',
          top: 0,
          left: 0,
          width: '100%',
          height: '100%',
          zIndex: -1,
          background: 'linear-gradient(-45deg, rgba(0, 0, 0, 0.3), rgba(0, 0, 0, 0.6))',
        }}
      />
      <Helmet
        link={[
          { rel: 'preload', href: heroBackgroundImageJpg, as: 'image', fetchpriority: 'high' },
          { rel: 'preload', href: heroBackgroundImageWebp, as: 'image', fetchpriority: 'high' },
        ]}
      />
      <Stack spacing={2}>
        {heading}
        {subHeading}
        {searchBar}
        <FlagButtons />
      </Stack>

      {downArrowCordova}
    </Container>
  );
};

SectionHeroComponent.displayName = 'SectionHero';
const SectionHero = memo(SectionHeroComponent);
export default SectionHero;
